import { memo, useCallback, useMemo } from "react";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { learnCardPresentationKey } from "@-/dev.improve.haniverse.lib.learn.card-action";
import type { LearnDeckRenderSlot } from "@-/dev.improve.haniverse.lib.learn.deck";
import { MorphemeInlineNavigationContext } from "@-/dev.improve.haniverse.lib.morpheme.page.navigator";
import {
  CardScrollOptInContext,
  type useCardScrollOptInState,
} from "@-/dev.improve.haniverse.lib.cards.review-swipe";
import {
  LearnCardControlsPresentationContext,
  LearnDeckContext,
  type LearnFlashcardExplorationDestination,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { LearnFlashcardExploration } from "@-/dev.improve.haniverse.routes.nav.app.learn._components.card-exploration";
import { LearnLessonFlashcard } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck._components.lesson-card";
import { LearnQuizFlashcard } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck._components.quiz-card";
import { LearnReviewFlashcard } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck._components.review-card";
import { SlideFlashcard } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.slide.index";

type LearnDeckFlashcardSlotProps = Omit<
  Parameters<typeof LearnReviewFlashcard>[0],
  "canRetireViews" | "exploration" | "slot"
> &
  LearnDeckRenderSlot & {
    scrollOptIn: ReturnType<typeof useCardScrollOptInState>["value"];
  };

/**
 * Preserve the unchanged card tree when a different slot reports readiness or admits a face.
 *
 * Reference links inside every card kind open within the card (the deck's exploration) instead of
 * pushing a route, so exploring never leaves the card stage.
 */
// oxlint-disable-next-line no-top/no-top-level-side-effects -- React.memo creates a component descriptor without running the component or changing external state.
export const LearnDeckFlashcardSlot = /* @__PURE__ */ memo(function LearnDeckFlashcardSlot({
  isCurrentPresentation,
  isDeckFocused,
  item,
  onSurfacesReleased,
  presentation,
  scrollOptIn,
  ...props
}: LearnDeckFlashcardSlotProps) {
  const slot = { item, presentation };
  const [exploreDeck, closeExploration, deckExploration] = useContextSelectors(
    LearnDeckContext,
    (c) => c.explore,
    (c) => c.closeExploration,
    (c) => c.exploration,
  );
  const presentationKey = learnCardPresentationKey(presentation);
  const explore = useCallback(
    (destination: LearnFlashcardExplorationDestination) =>
      exploreDeck(presentationKey, destination),
    [exploreDeck, presentationKey],
  );
  // The deck's exploration always belongs to its current presentation.
  const destination = isCurrentPresentation ? deckExploration : null;
  const exploration = useMemo(
    () =>
      destination === null ? null : (
        <LearnFlashcardExploration
          destination={destination}
          onBack={closeExploration}
          stage={props.stage}
        />
      ),
    [closeExploration, destination, props.stage],
  );
  return (
    <LearnCardControlsPresentationContext.Provider value={slot.presentation}>
      <MorphemeInlineNavigationContext value={explore}>
        <CardScrollOptInContext value={scrollOptIn}>
          {slot.item.kind === "slide" ? (
            <SlideFlashcard
              {...props}
              exploration={exploration}
              slot={{ ...slot, item: slot.item }}
            />
          ) : slot.item.kind === "quiz" ? (
            <LearnQuizFlashcard
              {...props}
              exploration={exploration}
              slot={{ ...slot, item: slot.item }}
            />
          ) : slot.item.kind === "lesson" ? (
            <LearnLessonFlashcard
              {...props}
              exploration={exploration}
              slot={{ ...slot, item: slot.item }}
            />
          ) : (
            <LearnReviewFlashcard
              {...props}
              canRetireViews={false}
              exploration={exploration}
              isCurrentPresentation={isCurrentPresentation}
              isDeckFocused={isDeckFocused}
              onSurfacesReleased={onSurfacesReleased}
              slot={{ ...slot, item: slot.item }}
            />
          )}
        </CardScrollOptInContext>
      </MorphemeInlineNavigationContext>
    </LearnCardControlsPresentationContext.Provider>
  );
});
